import { db, type StoredPageDelivery } from '../platform/webStorage'
import { afterFailure, BLOCKED_RETRY_DELAYS_MS, classifyFailure } from './outboxPolicy'
import { withRequestDeadline, throwIfAborted } from './requestDeadline'
import { syncLog } from './syncLog'

/** Sends only pages with journaled edits, two at a time, without opening editors. */
export class BackgroundPageDelivery {
  private running: Promise<void> | null = null
  private controller: AbortController | null = null
  private timer: ReturnType<typeof setTimeout> | null = null
  constructor(private hooks: {
    ready: () => boolean
    prepare: (signal: AbortSignal) => Promise<void>
    send: (docId: string, signal: AbortSignal) => Promise<void>
    changed: () => Promise<unknown>
  }) {}

  stop(): void {
    this.controller?.abort()
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
  }
  async retry(docId?: string): Promise<void> {
    if (docId) await db.pageDelivery.delete(docId)
    else await db.pageDelivery.clear()
    await this.nudge()
  }
  nudge(): Promise<void> {
    if (this.running) return this.running
    if (!this.hooks.ready()) return Promise.resolve()
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
    const controller = new AbortController()
    this.controller = controller
    this.running = this.run(controller.signal).catch(cause => {
      if (!controller.signal.aborted) this.logFailure(cause, 'Background sync coordinator failed')
    }).finally(async () => {
      await this.hooks.changed().catch(() => {})
      this.running = null
      this.controller = null
      if (!this.hooks.ready()) return
      let delay: number
      try {
        const ids = await db.yjsOutbox.orderBy('docId').uniqueKeys() as string[]
        const states = await db.pageDelivery.bulkGet(ids)
        const due = states.filter(state => state?.status !== 'failed').map(state => state?.nextAttemptAt ?? Date.now() + 5_000)
        if (!due.length) return
        delay = Math.max(1_000, Math.min(...due) - Date.now())
      } catch (cause) {
        this.logFailure(cause, 'Could not inspect the background sync queue')
        delay = 5_000
      }
      // A journal commit can start another pass while this inspection awaits IDB.
      if (!this.running && this.hooks.ready()) this.timer = setTimeout(() => { void this.nudge() }, delay)
    })
    return this.running
  }

  private logFailure(cause: unknown, context: string, docId?: string): void {
    syncLog.record({ direction: 'out', level: 'warn', kind: 'background-delivery-failed',
      message: `${context}: ${cause instanceof Error ? cause.message : String(cause)}`,
      ...(docId ? { targetType: 'page', targetIds: [docId] } : {}),
    })
  }

  private async recordFailure(docId: string, cause: unknown, signal: AbortSignal, context?: string): Promise<void> {
    if (signal.aborted || !this.hooks.ready()) return
    const previous = await db.pageDelivery.get(docId)
    if (previous?.status === 'failed') return
    const failure = afterFailure(previous || {}, cause, Date.now())
    const blocked = classifyFailure(cause) === 'blocked'
    const state: StoredPageDelivery = { docId, ...failure, status: failure.status === 'failed' ? 'failed' : 'pending',
      ...(blocked ? { status: 'pending', nextAttemptAt: Date.now() + BLOCKED_RETRY_DELAYS_MS[Math.min(previous?.attempts ?? 0, BLOCKED_RETRY_DELAYS_MS.length - 1)] } : {}),
    }
    if (context) state.lastError = `${context}: ${state.lastError}`
    await db.pageDelivery.put(state)
    this.logFailure(cause, context || 'Background page delivery failed', docId)
    await this.hooks.changed().catch(() => {})
  }

  private async run(signal: AbortSignal): Promise<void> {
    const ids = await db.yjsOutbox.orderBy('docId').uniqueKeys() as string[]
    try {
      await withRequestDeadline(bounded => this.hooks.prepare(bounded), { signal, timeoutMs: 30_000 })
    } catch (cause) {
      for (const docId of ids) await this.recordFailure(docId, cause, signal, 'Background sync preparation failed')
      throw cause
    }
    throwIfAborted(signal)
    // Preparation may have restored or committed additional journal entries.
    const preparedIds = await db.yjsOutbox.orderBy('docId').uniqueKeys() as string[]
    let index = 0
    const worker = async () => {
      while (index < preparedIds.length && this.hooks.ready() && !signal.aborted) {
        const docId = preparedIds[index++]
        const previous = await db.pageDelivery.get(docId)
        if (previous?.status === 'failed' || (previous?.nextAttemptAt ?? 0) > Date.now()) continue
        const send = async () => {
          if (!this.hooks.ready() || signal.aborted) return
          await withRequestDeadline(bounded => this.hooks.send(docId, bounded), { signal, timeoutMs: 45_000 })
          throwIfAborted(signal)
          await db.pageDelivery.delete(docId)
        }
        try {
          if (navigator.locks?.request) await navigator.locks.request(`ink-page-delivery:${docId}`, { ifAvailable: true }, lock => {
            if (!lock) throw new Error('Another app window is sending this page; background sync will retry automatically.')
            return send()
          })
          else await send()
        } catch (cause) {
          await this.recordFailure(docId, cause, signal)
        }
      }
    }
    await Promise.all([worker(), worker()])
  }
}
